import { useState, useRef, useEffect } from 'react';
import Button from '@/components/ui/Button';
import { Send, AlertCircle, Lightbulb, PhoneCall } from 'lucide-react';
import { RealTimeCoachingInput } from '@/components/RealTimeCoachingInput';
import { roleplayService } from '@/services/practiceService';

export default function RolePlayConversation({ session, onSendMessage, onComplete, isLoading }) {
  const [userMessage, setUserMessage] = useState('');
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [session?.conversation_history]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!userMessage.trim()) {
      setError('Please enter a message');
      return;
    }

    onSendMessage(userMessage.trim());
    setUserMessage('');
  };

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading session...</p>
      </div>
    );
  }

  const conversationHistory = session.conversation_history || [];
  const isPhoneSequence = session.practice_type === 'phone_sequence';
  const phoneSequenceData = session.phone_sequence_data;
  const currentPhoneStage = isPhoneSequence 
    ? roleplayService.getPhoneSequenceStage(phoneSequenceData?.currentStage || 0)
    : null;

  return (
    <div className="w-full h-full flex flex-col max-w-3xl mx-auto">
      {/* Session Info Header */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <div className="flex items-start justify-between mb-2">
          <div>
            {isPhoneSequence ? (
              <>
                <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider flex items-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  Stage {currentPhoneStage?.number}/6: {currentPhoneStage?.title}
                </p>
                <p className="text-sm text-blue-700 font-medium mt-1">{currentPhoneStage?.instruction}</p>
              </>
            ) : (
              <>
                <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">
                  {session.sales_stage?.charAt(0).toUpperCase() + session.sales_stage?.slice(1)} Stage
                </p>
                <p className="text-sm text-blue-700 font-medium">{session.scenario_briefing}</p>
              </>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            session.difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' :
            session.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
            session.difficulty_level === 'advanced' ? 'bg-orange-100 text-orange-700' :
            'bg-red-100 text-red-700'
          }`}>
            {session.difficulty_level?.charAt(0).toUpperCase() + session.difficulty_level?.slice(1)}
          </span>
        </div>
      </div>

      {/* Conversation History */}
      <div className="flex-1 overflow-y-auto mb-6 space-y-4 p-4 bg-gray-50 rounded-lg">
        {conversationHistory.length === 0 ? (
          <div className="text-center py-12">
            <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No messages yet</p>
            <p className="text-xs text-gray-400">Start the conversation with your opening</p>
          </div>
        ) : (
          <>
            {conversationHistory.map((msg, idx) => (
              <div key={idx} className="space-y-2">
                {/* User Message */}
                {msg.role === 'user' && (
                  <div className="flex justify-end">
                    <div className="max-w-xs lg:max-w-md bg-blue-600 text-white rounded-lg p-3">
                      <p className="text-sm">{msg.content}</p>
                      {msg.technique_used && (
                        <p className="text-xs opacity-75 mt-2">
                          📊 Technique: {msg.technique_used}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Prospect Response */}
                {msg.role === 'assistant' && (
                  <div className="flex justify-start">
                    <div className="max-w-xs lg:max-w-md bg-white border border-gray-200 rounded-lg p-3">
                      <p className="text-sm text-gray-900">{msg.content}</p>
                      {msg.technique_detected && (
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                          <p className="text-xs font-semibold text-gray-700">Feedback:</p>
                          <p className="text-xs text-gray-600">
                            🎯 Detected: <span className="font-medium">{msg.technique_detected}</span>
                          </p>
                          {msg.effectiveness_rating !== undefined && (
                            <p className="text-xs text-gray-600">
                              ⭐ Effectiveness: <span className="font-medium">{msg.effectiveness_rating}/10</span>
                            </p>
                          )}
                          {msg.feedback && (
                            <p className="text-xs text-gray-600 mt-2">{msg.feedback}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Phone Sequence Stage Coaching Tips */}
      {isPhoneSequence && currentPhoneStage && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
          <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Stage Focus: {currentPhoneStage.coachingFocus}</p>
            <p className="text-xs text-blue-700 opacity-90">{currentPhoneStage.instruction}</p>
          </div>
        </div>
      )}

      {/* Message Input with Real-Time Coaching */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <RealTimeCoachingInput
          value={userMessage}
          onChange={setUserMessage}
          onSubmit={() => handleSubmit({ preventDefault: () => {} })}
          placeholder="Type your response to the prospect... Focus on questions, not monologues"
          personaType={session.buyer_persona || 'CFO'}
          disabled={isLoading}
          minLength={10}
          maxLength={300}
        />

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={isLoading || !userMessage.trim()}
            className="flex-1"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </Button>
          <Button
            variant="outline"
            onClick={onComplete}
            disabled={isLoading || conversationHistory.length === 0}
          >
            End Session
          </Button>
        </div>
      </form>

      {isLoading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border border-blue-300 border-t-blue-600" />
          <p className="text-sm text-blue-700">Prospect is responding...</p>
        </div>
      )}
    </div>
  );
}
